import { useQuery } from "@tanstack/react-query";

import { validationAgentService } from "../services/validationAgent.service";
import { ValidationType } from "../domain/event.interface";

/**
 * The agents that may be bound to an event of this validation type — SIMS-8223.
 *
 * 🔴 `validationType` is in the QUERY KEY, not just the request. Without it,
 * changing an event's validation type would serve the previous type's catalog
 * from cache — and the designer would be offered agents that cannot validate
 * the event they are configuring, which is precisely what AC4 forbids. Same
 * trap that made account branding look broken on SIMS-9056.
 */
export const useValidationAgents = (validationType?: ValidationType) =>
	useQuery({
		queryKey: ["validation-agents", validationType],
		queryFn: () => validationAgentService.getValidationAgents(validationType),
		enabled: !!validationType,
		staleTime: 5 * 60 * 1000,
	});

/**
 * The WHOLE catalog, including entries this event cannot use.
 *
 * 🔴 A second query, and not redundant. `useValidationAgents` returns what may
 * be NEWLY bound — the server applies parity (is the implementation running?)
 * and status, neither of which the client can know. But describing an EXISTING
 * binding needs the opposite: the record of an agent that is deliberately NOT
 * offered, because it is incompatible with the event's current validation type.
 *
 * Without this the incompatible state of AC4 is unreachable: the bound agent is
 * absent from the filtered list, so nothing can say "Meal photo agent supports
 * Photo and Compound events" — which is exactly what the design asks for.
 */
export const useValidationAgentCatalog = () =>
	useQuery({
		queryKey: ["validation-agents", "all"],
		queryFn: () => validationAgentService.getValidationAgents(),
		staleTime: 5 * 60 * 1000,
	});
